'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MicroInfoCardsBody } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { PopupHeader, btnPrimary, btnSecondary, padded } from '../chrome';

/**
 * Swipeable / clickable card-stack. Canonical {@code body.steps[]}
 * ({@code {title, body}}) → one tile each. Last-card-reached triggers
 * {@code onComplete}.
 */
export function InfoCardsRenderer({ item, onComplete }: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const body = (item.body ?? {}) as MicroInfoCardsBody;
  const cards = body.steps ?? [];
  const [idx, setIdx] = useState(0);
  const card = cards[idx];

  const next = () => {
    if (idx >= cards.length - 1) {
      // "Finish" is terminal here — there is no results screen to stay on,
      // so the popup closes with the completion instead of stranding the
      // learner on the last card. `close` is ignored when this deck is a
      // learning-path step; the popup returns to the path there.
      onComplete(undefined, { close: true });
      return;
    }
    setIdx((i) => i + 1);
  };

  if (cards.length === 0 || !card) {
    return (
      <div style={padded}>
        <PopupHeader kind={item.kind} title={item.title} description={item.description} />
        <div style={{ marginTop: 16, color: 'var(--color-text-secondary, #71717a)' }}>
          {t('microInfoCards.empty')}
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupHeader
        kind={item.kind}
        title={item.title}
        meta={
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'var(--color-text-tertiary, #94867d)',
            }}
          >
            {t('microInfoCards.counter', { current: idx + 1, total: cards.length })}
          </div>
        }
      />
      <div style={{ ...padded, paddingTop: 0 }}>
        <div
          style={{
            border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
            borderRadius: 12,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            minHeight: 200,
          }}
        >
          {card.title && (
            <div style={{ fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600 }}>
              {card.title}
            </div>
          )}
          {card.body && (
            <div style={{ fontSize: 'var(--font-size-sm, 14px)', lineHeight: 1.6 }}>
              {card.body}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            style={btnSecondary}
          >
            {t('microInfoCards.back')}
          </button>
          <button type="button" onClick={next} style={btnPrimary}>
            {idx === cards.length - 1 ? t('microInfoCards.finish') : t('microInfoCards.next')}
          </button>
        </div>
      </div>
    </>
  );
}
